'use client';

import { TBlogCategoryItemized } from '@/app/(blogposts)/blog/blog.types';
import { LoadingSpinnerIcon } from '@/assets/icons/LoadingSpinner';
import { TextInput } from '@/components/inputs/TextInput';
import { Button } from '@/components/ui/Button';
import { Text } from '@/components/ui/Text';
import clsx from 'clsx';
import { SearchIcon } from 'lucide-react';
import Link from 'next/link';
import React, { useCallback, useRef, useState } from 'react';
import { FocusOn } from 'react-focus-on';
import { ExitIcon } from '../ExitIcon';
import { useBlogHeaderSearch } from '../useBlogHeaderSearch';

import './BlogHeaderMobile.scss';

type Props = {
  categories: TBlogCategoryItemized[];
  className?: string;
  title: string;
};

const FilterMenuLogo: React.FC = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3.75 7C3.75 6.80109 3.82902 6.61032 3.96967 6.46967C4.11032 6.32902 4.30109 6.25 4.5 6.25H19.5C19.6989 6.25 19.8897 6.32902 20.0303 6.46967C20.171 6.61032 20.25 6.80109 20.25 7C20.25 7.19891 20.171 7.38968 20.0303 7.53033C19.8897 7.67098 19.6989 7.75 19.5 7.75H4.5C4.30109 7.75 4.11032 7.67098 3.96967 7.53033C3.82902 7.38968 3.75 7.19891 3.75 7ZM6.25 12C6.25 11.8011 6.32902 11.6103 6.46967 11.4697C6.61032 11.329 6.80109 11.25 7 11.25H17C17.1989 11.25 17.3897 11.329 17.5303 11.4697C17.671 11.6103 17.75 11.8011 17.75 12C17.75 12.1989 17.671 12.3897 17.5303 12.5303C17.3897 12.671 17.1989 12.75 17 12.75H7C6.80109 12.75 6.61032 12.671 6.46967 12.5303C6.32902 12.3897 6.25 12.1989 6.25 12ZM9.25 17C9.25 16.8011 9.32902 16.6103 9.46967 16.4697C9.61032 16.329 9.80109 16.25 10 16.25H14C14.1989 16.25 14.3897 16.329 14.5303 16.4697C14.671 16.6103 14.75 16.8011 14.75 17C14.75 17.1989 14.671 17.3897 14.5303 17.5303C14.3897 17.671 14.1989 17.75 14 17.75H10C9.80109 17.75 9.61032 17.671 9.46967 17.5303C9.32902 17.3897 9.25 17.1989 9.25 17Z'
      fill='black'
    />
  </svg>
);

enum Mode {
  Resting = 'rest',
  Open = 'open',
}

export const BlogHeaderMobile: React.FC<Props> = ({
  className,
  categories,
  title,
}) => {
  const {
    searchTerm,
    handleSearch,
    handleKeyDown,
    handleSearchTermChange,
    loading,
  } = useBlogHeaderSearch();
  const headerRef = useRef<HTMLDivElement | null>(null);

  const [mode, setMode] = useState(Mode.Resting);
  const [menuHeight, setMenuHeight] = useState<number | undefined>(undefined);

  const calculateMenuHeight = useCallback(() => {
    if (!headerRef.current) return;

    const { top, height } = headerRef.current.getBoundingClientRect();

    setMenuHeight(window.innerHeight - top - height);
  }, []);

  const handleClick = useCallback(() => {
    if (mode === Mode.Resting) {
      calculateMenuHeight();

      setMode(Mode.Open);
    }

    if (mode === Mode.Open) {
      setMode(Mode.Resting);
    }
  }, [calculateMenuHeight, mode]);

  return (
    <div className={clsx('BlogHeaderMobile', className)}>
      <FocusOn
        enabled={mode === Mode.Open}
        onClickOutside={() => setMode(Mode.Resting)}
        allowPinchZoom
      >
        <div className='BlogHeaderMobile__header' ref={headerRef}>
          <Link href='/blog'>
            <Text className='BlogHeaderMobile__title' variant='h3-serif'>
              {title}
            </Text>
          </Link>

          <div>
            <Button
              className='BlogHeaderMobile__cta'
              onClick={handleClick}
              variant='ghost'
            >
              {mode === Mode.Resting && <FilterMenuLogo />}

              {mode === Mode.Open && <ExitIcon />}
            </Button>
          </div>
        </div>

        <div
          className={clsx('BlogHeaderMobile__menu', {
            hidden: mode === Mode.Resting,
          })}
          style={{ height: menuHeight }}
        >
          <div className='BlogHeaderMobile__menu-content'>
            <div className='BlogHeaderMobile__menu-search-bar'>
              <div className='BlogHeaderMobile__menu-search-bar-input-wrapper'>
                <TextInput
                  className='BlogHeaderMobile__menu-search-bar-input pr-16'
                  placeholder='Search'
                  name='search'
                  onChange={handleSearchTermChange}
                  onKeyDown={handleKeyDown}
                  readOnly={loading}
                  value={searchTerm}
                  type='text'
                />

                <div className='BlogHeaderMobile__menu-search-bar-icon'>
                  <Button onClick={handleSearch} variant='ghost'>
                    <SearchIcon size={24} />
                  </Button>
                </div>

                {loading && (
                  <div className='BlogHeaderMobile__loading-wrapper'>
                    <LoadingSpinnerIcon />
                  </div>
                )}
              </div>
            </div>

            <nav className='BlogHeaderMobile__categories-and-search-results-area'>
              <Link
                className='BlogHeaderMobile__categories-and-search-results-area-item'
                href={`/blog`}
              >
                <Text variant='h3'>All Topics</Text>
              </Link>
              {categories.map((category, index) => (
                <Link
                  className='BlogHeaderMobile__categories-and-search-results-area-item'
                  key={`${index}::${category.item.id}`}
                  href={`/blog/category/${category.item.categorySlug}`}
                >
                  <Text variant='h3'>{category.item.title}</Text>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </FocusOn>
    </div>
  );
};
